import { FileText } from "lucide-react";
import type { Product, ProductVariant } from "@sakura-tech-lab/dashboard-api";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { isIngestionActive } from "@/lib/ingestion-progress";
import { getProductDescription } from "./product-detail-utils";
import { StructuredDescription } from "./StructuredDescription";

interface ProductDescriptionCardProps {
  readonly product: Product;
  readonly selectedVariant: ProductVariant | null;
  readonly language: string;
}

/**
 * Description card shown in the detail page's main column. While the pipeline
 * is still running and no description exists yet, a skeleton placeholder
 * conveys that the copy is on its way.
 */
export function ProductDescriptionCard({
  product,
  selectedVariant,
  language,
}: ProductDescriptionCardProps) {
  const description = getProductDescription(product, selectedVariant, language);

  if (!description) {
    if (!isIngestionActive(product)) return null;

    return (
      <Card className="min-w-0">
        <CardHeader className="p-5 pb-3 sm:p-6 sm:pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-violet-100 text-violet-600 dark:bg-violet-950/60 dark:text-violet-400">
              <FileText aria-hidden="true" className="h-4 w-4" />
            </span>
            Description
          </CardTitle>
          <CardDescription>Generated during AI enrichment — check back shortly.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 p-5 pt-0 sm:p-6 sm:pt-0">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-11/12" />
          <Skeleton className="h-4 w-4/6" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="min-w-0">
      <CardHeader className="p-5 pb-3 sm:p-6 sm:pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-violet-100 text-violet-600 dark:bg-violet-950/60 dark:text-violet-400">
            <FileText aria-hidden="true" className="h-4 w-4" />
          </span>
          Description
        </CardTitle>
      </CardHeader>
      <CardContent className="p-5 pt-0 sm:p-6 sm:pt-0">
        <StructuredDescription text={description} />
      </CardContent>
    </Card>
  );
}
